import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  FaCalendarAlt,
  FaCheck,
  FaClock,
  FaEdit,
  FaInfoCircle,
  FaLeaf,
  FaRedoAlt,
  FaSave,
  FaSeedling,
  FaTimes,
} from 'react-icons/fa';
import { ThemeContext } from '../App';
import { fenologiaCultural } from '../constants/fenologiaCultural';
import { culturaParaFenologia } from '../constants/culturas';
import { supabase } from '../supabaseClient';
import './propriedades-platform.css';

const DIA_EM_MS = 24 * 60 * 60 * 1000;
const FORRAGEIRAS_DE_MANEJO_CONTINUO = new Set(['braquiaria', 'capim_mombaca']);

function parseLocalDate(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const [year, month, day] = dateStr.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function toLocalISO(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDate(date) {
  return date.toLocaleDateString('pt-BR');
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

// Props: cultura (string), dataPlantio/dataColheita (yyyy-mm-dd), talhaoId e permission.
export default function Fenologia({
  cultura: culturaBruta,
  dataPlantio,
  dataColheita,
  talhaoId,
  permission = 'owner',
}) {
  const { darkMode } = useContext(ThemeContext);
  const cultura = culturaParaFenologia(culturaBruta);
  const [editandoIdx, setEditandoIdx] = useState(null);
  const [rascunho, setRascunho] = useState(null);
  const [datasAjustadas, setDatasAjustadas] = useState({});
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const podeAjustar = permission === 'owner' || permission === 'editor';

  useEffect(() => {
    if (!talhaoId) return undefined;
    let ativo = true;

    (async () => {
      try {
        const { data, error } = await supabase
          .from('fenologia_ajustada')
          .select('*')
          .eq('talhao_id', talhaoId);

        if (!ativo) return;
        if (error) {
          setErro(`Erro ao buscar ajustes: ${error.message}`);
          return;
        }

        const ajustadas = {};
        (data || []).forEach((row) => {
          ajustadas[row.fase_idx] = {
            inicioFase: row.inicio,
            fimFase: row.fim,
          };
        });
        setDatasAjustadas(ajustadas);
        setErro(null);
      } catch (error) {
        if (ativo) setErro(`Erro ao buscar ajustes: ${error.message}`);
      }
    })();

    return () => {
      ativo = false;
    };
  }, [talhaoId]);

  const dataReferencia = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const colheita = parseLocalDate(dataColheita);
    return colheita && colheita <= hoje ? colheita : hoje;
  }, [dataColheita]);

  const fases = useMemo(() => {
    if (!cultura || !fenologiaCultural[cultura] || !dataPlantio) return [];

    const fasesBase = fenologiaCultural[cultura];
    const resultado = [];
    let inicioFase = parseLocalDate(datasAjustadas[0]?.inicioFase) || parseLocalDate(dataPlantio);

    fasesBase.forEach((faseBase, idx) => {
      inicioFase = parseLocalDate(datasAjustadas[idx]?.inicioFase) || inicioFase;
      const fimAjustado = parseLocalDate(datasAjustadas[idx]?.fimFase);
      const fimFase = fimAjustado || new Date(inicioFase.getTime() + (faseBase.dias - 1) * DIA_EM_MS);
      const dias = Math.round((fimFase - inicioFase) / DIA_EM_MS) + 1;

      resultado.push({
        ...faseBase,
        dias,
        vigente: dataReferencia >= inicioFase && dataReferencia <= fimFase,
        inicioDate: inicioFase,
        fimDate: fimFase,
        inicioFase: formatDate(inicioFase),
        fimFase: formatDate(fimFase),
        inicioFaseISO: toLocalISO(inicioFase),
        fimFaseISO: toLocalISO(fimFase),
      });

      inicioFase = new Date(fimFase.getTime() + DIA_EM_MS);
    });

    return resultado;
  }, [cultura, dataPlantio, dataReferencia, datasAjustadas]);

  const resumo = useMemo(() => {
    if (!fases.length) return null;
    const plantio = parseLocalDate(dataPlantio);
    const colheita = parseLocalDate(dataColheita);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const safraEncerrada = Boolean(colheita && colheita <= hoje);
    const diasPlantado = plantio
      ? Math.max(0, Math.floor((dataReferencia - plantio) / DIA_EM_MS))
      : 0;
    const faseAtualIdx = fases.findIndex((fase) => fase.vigente);
    const inicioCiclo = fases[0].inicioDate;
    const fimCiclo = fases[fases.length - 1].fimDate;
    const duracaoEstimada = Math.max(1, Math.round((fimCiclo - inicioCiclo) / DIA_EM_MS) + 1);
    const progressoBruto = ((dataReferencia - inicioCiclo) / Math.max(DIA_EM_MS, fimCiclo - inicioCiclo)) * 100;
    const progressoCiclo = safraEncerrada ? 100 : Math.round(clamp(progressoBruto, 0, 100));

    let progressoFase = 0;
    let diasRestantes = null;
    if (faseAtualIdx >= 0) {
      const fase = fases[faseAtualIdx];
      const duracao = Math.max(DIA_EM_MS, fase.fimDate - fase.inicioDate);
      progressoFase = Math.round(clamp(((dataReferencia - fase.inicioDate) / duracao) * 100, 0, 100));
      diasRestantes = Math.max(0, Math.ceil((fase.fimDate - dataReferencia) / DIA_EM_MS));
    }

    return {
      diasPlantado,
      duracaoEstimada,
      faseAtualIdx,
      progressoCiclo,
      progressoFase,
      diasRestantes,
      safraEncerrada,
    };
  }, [dataColheita, dataPlantio, dataReferencia, fases]);

  if (!cultura || !fenologiaCultural[cultura]) {
    return <div className="phenology-empty-state">Cultura não selecionada ou não cadastrada.</div>;
  }

  if (!dataPlantio) {
    return <div className="phenology-empty-state">Informe a data de plantio para visualizar as fases.</div>;
  }

  const culturaLabel = cultura.charAt(0).toUpperCase() + cultura.slice(1);
  const faseAtual = resumo.faseAtualIdx >= 0 ? fases[resumo.faseAtualIdx] : null;
  const pastagemEstabelecida = FORRAGEIRAS_DE_MANEJO_CONTINUO.has(cultura)
    && !resumo.safraEncerrada
    && !faseAtual
    && dataReferencia > fases[fases.length - 1].fimDate;

  function abrirEdicao(idx) {
    const fase = fases[idx];
    setRascunho({
      inicioFase: datasAjustadas[idx]?.inicioFase || fase.inicioFaseISO,
      fimFase: datasAjustadas[idx]?.fimFase || fase.fimFaseISO,
    });
    setEditandoIdx(idx);
    setErro(null);
  }

  function cancelarEdicao() {
    setEditandoIdx(null);
    setRascunho(null);
  }

  function montarAjustesEncadeados(idx) {
    const novosAjustes = { ...datasAjustadas };
    novosAjustes[idx] = { ...rascunho };

    if (idx > 0) {
      const inicioAtual = parseLocalDate(rascunho.inicioFase);
      const fimAnterior = new Date(inicioAtual.getTime() - DIA_EM_MS);
      novosAjustes[idx - 1] = {
        inicioFase: novosAjustes[idx - 1]?.inicioFase || fases[idx - 1].inicioFaseISO,
        fimFase: toLocalISO(fimAnterior),
      };
    }

    let inicioProxima = new Date(parseLocalDate(rascunho.fimFase).getTime() + DIA_EM_MS);
    for (let i = idx + 1; i < fases.length; i += 1) {
      const diasFase = fenologiaCultural[cultura][i]?.dias || 1;
      const fimFase = new Date(inicioProxima.getTime() + (diasFase - 1) * DIA_EM_MS);
      novosAjustes[i] = {
        inicioFase: toLocalISO(inicioProxima),
        fimFase: toLocalISO(fimFase),
      };
      inicioProxima = new Date(fimFase.getTime() + DIA_EM_MS);
    }

    return novosAjustes;
  }

  async function salvarAjustes(idx) {
    if (!rascunho?.inicioFase || !rascunho?.fimFase) {
      setErro('Informe as datas de início e fim da fase.');
      return;
    }

    if (parseLocalDate(rascunho.fimFase) < parseLocalDate(rascunho.inicioFase)) {
      setErro('A data final deve ser igual ou posterior à data inicial.');
      return;
    }

    const novosAjustes = montarAjustesEncadeados(idx);
    setSalvando(true);

    try {
      if (talhaoId) {
        const ajustesParaSalvar = Object.entries(novosAjustes)
          .filter(([, ajuste]) => ajuste?.inicioFase && ajuste?.fimFase)
          .map(([faseIdx, ajuste]) => ({
            talhao_id: talhaoId,
            fase_idx: Number(faseIdx),
            inicio: ajuste.inicioFase,
            fim: ajuste.fimFase,
          }));

        const { error } = await supabase
          .from('fenologia_ajustada')
          .upsert(ajustesParaSalvar, { onConflict: 'talhao_id,fase_idx' });
        if (error) throw error;
      }

      setDatasAjustadas(novosAjustes);
      setErro(null);
      cancelarEdicao();
    } catch (error) {
      setErro(`Erro ao salvar ajustes: ${error.message}`);
    } finally {
      setSalvando(false);
    }
  }

  async function restaurarFase(idx) {
    try {
      if (talhaoId) {
        const { error } = await supabase
          .from('fenologia_ajustada')
          .delete()
          .eq('talhao_id', talhaoId)
          .eq('fase_idx', idx);
        if (error) throw error;
      }

      setDatasAjustadas((anteriores) => {
        const proximas = { ...anteriores };
        delete proximas[idx];
        return proximas;
      });
      setErro(null);
    } catch (error) {
      setErro(`Erro ao restaurar ajuste: ${error.message}`);
    }
  }

  return (
    <section className={`phenology-cycle-card${darkMode ? ' is-dark' : ''}`}>
      <header className="phenology-cycle-header">
        <div>
          <span className="phenology-eyebrow"><FaSeedling /> Ciclo da cultura</span>
          <h3>Fases fenológicas</h3>
          <p>Acompanhe a evolução do talhão e os próximos marcos do ciclo.</p>
        </div>
        <span className={`phenology-cycle-status ${resumo.safraEncerrada ? 'is-finished' : 'is-running'}`}>
          {resumo.safraEncerrada ? <FaCheck /> : <FaLeaf />}
          {resumo.safraEncerrada ? 'Ciclo concluído' : pastagemEstabelecida ? 'Manejo contínuo' : 'Em desenvolvimento'}
        </span>
      </header>

      <div className="phenology-cycle-overview">
        <div className="phenology-cycle-metrics">
          <div className="phenology-main-metric">
            <span>{resumo.safraEncerrada ? 'Duração registrada' : 'Dias após o plantio'}</span>
            <strong>{resumo.diasPlantado}</strong>
            <small>dias</small>
          </div>
          <div className="phenology-current-phase">
            <span>{resumo.safraEncerrada ? 'Cultura' : 'Fase atual'}</span>
            <strong>{resumo.safraEncerrada ? culturaLabel : pastagemEstabelecida ? 'Pastagem estabelecida' : (faseAtual?.fase || 'Fora do ciclo estimado')}</strong>
            {!resumo.safraEncerrada && faseAtual && (
              <small>
                {resumo.diasRestantes === 0
                  ? 'Transição prevista para hoje'
                  : `${resumo.diasRestantes} ${resumo.diasRestantes === 1 ? 'dia restante' : 'dias restantes'} nesta fase`}
              </small>
            )}
            {pastagemEstabelecida && <small>Implantação concluída. Acompanhe ocupação, descanso e altura do pasto na Pecuária.</small>}
          </div>
        </div>

        <div className="phenology-cycle-progress" aria-label={`Progresso estimado do ciclo: ${resumo.progressoCiclo}%`}>
          <div className="phenology-progress-labels">
            <span>{pastagemEstabelecida ? 'Estabelecimento da pastagem' : 'Progresso estimado do ciclo'}</span>
            <strong>{resumo.progressoCiclo}%</strong>
          </div>
          <div className="phenology-progress-track">
            <span style={{ width: `${resumo.progressoCiclo}%` }} />
          </div>
          <div className="phenology-progress-caption">
            <span>Plantio</span>
            <span>{resumo.safraEncerrada
              ? `Colhido em ${formatDate(parseLocalDate(dataColheita))}`
              : pastagemEstabelecida
                ? `Implantação estimada em ${resumo.duracaoEstimada} dias`
                : `${resumo.duracaoEstimada} dias estimados`}</span>
          </div>
        </div>
      </div>

      <div className="phenology-section-heading">
        <div>
          <h4>Linha do tempo</h4>
          <p>{fases.length} etapas previstas para {culturaLabel}</p>
        </div>
        <span className="phenology-culture-chip"><FaLeaf /> {culturaLabel}</span>
      </div>

      <ol className="phenology-timeline">
        {fases.map((fase, idx) => {
          const concluida = dataReferencia > fase.fimDate || resumo.safraEncerrada;
          const atual = fase.vigente && !resumo.safraEncerrada;
          const status = concluida ? 'is-completed' : (atual ? 'is-current' : 'is-upcoming');
          const ajustada = Boolean(datasAjustadas[idx]);

          return (
            <li className={`phenology-stage ${status}`} key={`${fase.fase}-${idx}`}>
              <div className="phenology-stage-rail" aria-hidden="true">
                <span>{concluida ? <FaCheck /> : idx + 1}</span>
              </div>

              <article className="phenology-stage-card">
                <div className="phenology-stage-header">
                  <div>
                    <div className="phenology-stage-title-row">
                      <h5>{fase.fase}</h5>
                      <span className="phenology-stage-badge">
                        {concluida ? 'Concluída' : (atual ? 'Fase atual' : 'Próxima')}
                      </span>
                      {ajustada && <span className="phenology-adjusted-badge">Datas ajustadas</span>}
                    </div>
                    <span className="phenology-duration"><FaClock /> {fase.dias} {fase.dias === 1 ? 'dia' : 'dias'} de duração</span>
                  </div>

                  {podeAjustar && editandoIdx !== idx && (
                    <div className="phenology-stage-actions">
                      <button type="button" className="phenology-action-button" onClick={() => abrirEdicao(idx)}>
                        <FaEdit /> Ajustar datas
                      </button>
                      {ajustada && (
                        <button
                          type="button"
                          className="phenology-icon-button"
                          onClick={() => restaurarFase(idx)}
                          aria-label={`Restaurar datas padrão de ${fase.fase}`}
                          title="Restaurar datas padrão"
                        >
                          <FaRedoAlt />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {editandoIdx === idx ? (
                  <div className="phenology-edit-panel">
                    <div className="phenology-edit-grid">
                      <label>
                        <span>Início da fase</span>
                        <div className="phenology-date-input">
                          <FaCalendarAlt />
                          <input
                            type="date"
                            value={rascunho?.inicioFase || ''}
                            onChange={(event) => setRascunho((atualRascunho) => ({ ...atualRascunho, inicioFase: event.target.value }))}
                          />
                        </div>
                      </label>
                      <label>
                        <span>Fim da fase</span>
                        <div className="phenology-date-input">
                          <FaCalendarAlt />
                          <input
                            type="date"
                            value={rascunho?.fimFase || ''}
                            onChange={(event) => setRascunho((atualRascunho) => ({ ...atualRascunho, fimFase: event.target.value }))}
                          />
                        </div>
                      </label>
                    </div>
                    <p>As fases seguintes serão recalculadas a partir deste intervalo.</p>
                    <div className="phenology-edit-actions">
                      <button type="button" className="phenology-save-button" onClick={() => salvarAjustes(idx)} disabled={salvando}>
                        <FaSave /> {salvando ? 'Salvando...' : 'Salvar ajuste'}
                      </button>
                      <button type="button" className="phenology-cancel-button" onClick={cancelarEdicao} disabled={salvando}>
                        <FaTimes /> Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="phenology-stage-dates">
                    <div>
                      <span>Início</span>
                      <strong>{fase.inicioFase}</strong>
                    </div>
                    <span className="phenology-date-line" aria-hidden="true" />
                    <div>
                      <span>Fim previsto</span>
                      <strong>{fase.fimFase}</strong>
                    </div>
                  </div>
                )}

                {atual && editandoIdx !== idx && (
                  <div className="phenology-stage-progress" aria-label={`Progresso da fase atual: ${resumo.progressoFase}%`}>
                    <div><span>Avanço nesta fase</span><strong>{resumo.progressoFase}%</strong></div>
                    <div className="phenology-stage-progress-track"><span style={{ width: `${resumo.progressoFase}%` }} /></div>
                  </div>
                )}
              </article>
            </li>
          );
        })}
      </ol>

      {erro && <div className="phenology-error" role="alert"><FaInfoCircle /> {erro}</div>}

      <footer className="phenology-note">
        <FaInfoCircle />
        <span>As datas são estimativas médias. Registre ajustes conforme as observações reais do ciclo no campo.</span>
      </footer>
    </section>
  );
}
